/**
 * PartsCounterPieChart Component
 *
 * Pie/Donut chart showing gross distribution among parts counter persons.
 * Uses green palette to match Parts Counter theme.
 */

import React, { useMemo } from 'react';
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import { PartsCounterPerson } from './types';

interface PartsCounterPieChartProps {
  persons: PartsCounterPerson[];
  totalGross: number;
}

/**
 * Green-based color palette for pie slices
 */
const COLORS = [
  '#16a34a', // green-600
  '#22c55e', // green-500
  '#4ade80', // green-400
  '#86efac', // green-300
  '#2563eb', // blue-600
  '#3b82f6', // blue-500
  '#60a5fa', // blue-400
  '#93c5fd', // blue-300
  '#f97316', // orange-500
  '#fb923c', // orange-400
  '#a78bfa', // violet-400
  '#c084fc', // violet-300
];

function formatCurrency(value: number): string {
  if (value >= 1000000) {
    return `$${(value / 1000000).toFixed(1)}M`;
  }
  if (value >= 1000) {
    return `$${(value / 1000).toFixed(0)}K`;
  }
  return `$${value.toFixed(0)}`;
}

const CustomTooltip = ({ active, payload }: any) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload;
    return (
      <div className="bg-white border border-gray-200 rounded-lg shadow-lg p-3 max-w-xs">
        <p className="font-semibold text-gray-900 mb-1 truncate">{data.name}</p>
        <p className="text-sm text-gray-600">
          Gross:{' '}
          <span className="font-medium text-green-600">
            {formatCurrency(data.value)}
          </span>
        </p>
        <p className="text-sm text-gray-600">
          Share: <span className="font-medium">{data.percentage.toFixed(1)}%</span>
        </p>
        <p className="text-sm text-gray-600">
          Tickets: <span className="font-medium">{data.transactionCount}</span>
        </p>
      </div>
    );
  }
  return null;
};

const renderLegendText = (value: string) => {
  const maxLength = 15;
  if (value.length > maxLength) {
    return value.substring(0, maxLength) + '...';
  }
  return value;
};

export const PartsCounterPieChart: React.FC<PartsCounterPieChartProps> = ({
  persons,
  totalGross,
}) => {
  const chartData = useMemo(() => {
    const MAX_SLICES = 8;

    if (persons.length === 0 || totalGross === 0) {
      return [];
    }

    const sorted = [...persons].sort(
      (a, b) => b.grossTotal - a.grossTotal
    );

    if (sorted.length <= MAX_SLICES) {
      return sorted.map((person, index) => ({
        name: person.employeeName,
        value: person.grossTotal,
        percentage: (person.grossTotal / totalGross) * 100,
        transactionCount: person.transactionCount,
        color: COLORS[index % COLORS.length],
      }));
    }

    const top = sorted.slice(0, MAX_SLICES - 1);
    const others = sorted.slice(MAX_SLICES - 1);
    const othersTotal = others.reduce((sum, p) => sum + p.grossTotal, 0);
    const othersTxnCount = others.reduce((sum, p) => sum + p.transactionCount, 0);

    const result = top.map((person, index) => ({
      name: person.employeeName,
      value: person.grossTotal,
      percentage: (person.grossTotal / totalGross) * 100,
      transactionCount: person.transactionCount,
      color: COLORS[index % COLORS.length],
    }));

    if (othersTotal > 0) {
      result.push({
        name: `Others (${others.length})`,
        value: othersTotal,
        percentage: (othersTotal / totalGross) * 100,
        transactionCount: othersTxnCount,
        color: '#9ca3af', // gray-400
      });
    }

    return result;
  }, [persons, totalGross]);

  if (chartData.length === 0) {
    return (
      <div className="h-64 flex items-center justify-center text-gray-500 text-sm">
        No data to display
      </div>
    );
  }

  return (
    <div className="h-64">
      <ResponsiveContainer width="100%" height="100%">
        <PieChart>
          <Pie
            data={chartData}
            cx="50%"
            cy="50%"
            innerRadius={45}
            outerRadius={75}
            paddingAngle={2}
            dataKey="value"
            nameKey="name"
          >
            {chartData.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={entry.color} />
            ))}
          </Pie>
          <Tooltip content={<CustomTooltip />} />
          <Legend
            layout="vertical"
            align="right"
            verticalAlign="middle"
            formatter={renderLegendText}
            wrapperStyle={{ fontSize: '12px', paddingLeft: '10px' }}
          />
        </PieChart>
      </ResponsiveContainer>
    </div>
  );
};
